import { useAnonAuth } from "@/hooks/useAnonAuth";
import { useChatHistory } from "@/hooks/useChatHistory";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { ChatInput } from "@/components/chat/ChatInput";
import { toast } from "sonner";

const ChatSection = () => {
  const token = useAnonAuth();
  const { messages, questionCount, lastQuestionDate, isLoading, setMessages, setQuestionCount, setLastQuestionDate } =
    useChatHistory(token);

  const DAILY_LIMIT = 5;

  const handleSend = async (question: string) => {
    if (!token) return toast.error("Not authenticated");
    if (questionCount >= DAILY_LIMIT) return toast.error("Daily limit reached");

    setMessages((prev: any) => [...prev, { role: "user", content: question }]);
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question }),
      });
      const data = await res.json();
      setMessages((prev: any) => [...prev, { role: "assistant", content: data.answer }]);
      setQuestionCount((c: number) => c + 1);
      setLastQuestionDate(new Date().toISOString());
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  return (
    <section id="chat" className="flex flex-col items-center justify-center py-16 bg-background text-foreground">
      <div className="w-full max-w-2xl flex flex-col rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 min-h-[420px] max-h-[60vh] overflow-y-auto p-6 bg-background/60">
          <ChatMessages messages={messages} isLoading={isLoading} />
        </div>

        {/* Footer / Input */}
        <div className="border-t border-border bg-card/70 backdrop-blur-sm p-4">
          <div className="text-sm text-muted-foreground mb-2 flex justify-between">
            <span>
              {questionCount}/{DAILY_LIMIT} questions used
              {lastQuestionDate && (
                <span className="ml-2 text-xs text-muted-foreground/70">
                  (Last asked:{" "}
                  {new Date(lastQuestionDate).toLocaleDateString("en-US", { timeZone: "America/New_York" })})
                </span>
              )}
            </span>
          </div>

          <ChatInput disabled={!token || questionCount >= DAILY_LIMIT} onSend={handleSend} />
        </div>
      </div>
    </section>
  );
};

export default ChatSection;
